import { Box, Typography } from "@mui/material";
import { band10Colors, legendColors, legendUnits } from "./const";
import { CustomValue } from "../Custom/CustomTypography";

export const convertDate = (inputDate) => {
  const date = new Date(inputDate);
  date.setDate(date.getDate() + 1);
  const options = { day: "2-digit", month: "long" };
  return date.toLocaleDateString("default", options);
};

export const renderHTMLContent = ({
  bandCache,
  selectedBand,
  farmSatelliteData,
}) => {
  return (
    <>
      <iframe
        srcDoc={bandCache[selectedBand]}
        style={{
          width: "100%",
          height: "500px",
          border: "none",
          marginTop: "10px",
        }}
        title={`Band ${selectedBand} Map`}
      />
      {renderLegend({ farmSatelliteData, selectedBand })}
    </>
  );
};

const renderLegend = ({ farmSatelliteData, selectedBand }) => {
  const selectedFile = farmSatelliteData.html_files.find(
    (file) => file.band === selectedBand
  );

  if (!selectedFile) return null;

  const { legend_data } = selectedFile;
  const unit = legendUnits[selectedBand] || "";

  // Function to process legend values
  const processLegendValue = (value) => {
    if (selectedBand === 9) {
      // For band 9, return log10 of the value, handling special cases
      return Math.log10(-1 * value).toFixed(4);
    }
    if (selectedBand === 2) {
      // For band 2, return value / 100, handling special cases
      return (value / 100).toFixed(4);
    }
    return value.toFixed(4);
  };

  // Reversing legend_data for all bands except band 12
  const legendDataToRender =
    selectedBand === 12 || selectedBand === 10
      ? legend_data
      : legend_data.slice().reverse();

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      mt={2}
      sx={{ backgroundColor: "#fff", padding: "10px 15px" }}
    >
      {legendDataToRender.map((value, index) => (
        <Box
          key={index}
          display="flex"
          flexDirection="row"
          alignItems="center"
          mb={1}
        >
          <Box
            width="20px"
            height="20px"
            bgcolor={
              selectedBand !== 10 ? legendColors[index] : band10Colors[index]
            }
            mr={2} // space between color box and value
          />
          {selectedBand !== 10 && (
            <Box display="flex" alignItems="center" minWidth="80px">
              <CustomValue>{processLegendValue(value)}</CustomValue>
            </Box>
          )}
          {selectedBand === 10 && <Typography>{value}</Typography>}
        </Box>
      ))}
      <Box mt={1}>
        <CustomValue>{unit}</CustomValue>
      </Box>
    </Box>
  );
};

export const convertIrrigationMmToDescription = (mm, t) => {
  const litersPerAcre = Math.round(mm * 4047); // 1 acre = 4047 square meters, 1mm = 1L/sqm
  const bucketsPerAcre = Math.round(litersPerAcre / 10); // 10L bucket estimate

  if (mm === 0) return { liters: 0, buckets: 0, description: t("None needed") };
  if (mm < 2)
    return {
      liters: litersPerAcre.toLocaleString(),
      buckets: Math.max(1, bucketsPerAcre),
      description: t("Very light sprinkle"),
    };
  if (mm < 5)
    return {
      liters: litersPerAcre.toLocaleString(),
      buckets: bucketsPerAcre,
      description: t("Light watering"),
    };
  if (mm < 10)
    return {
      liters: litersPerAcre.toLocaleString(),
      buckets: bucketsPerAcre,
      description: t("Normal watering"),
    };
  if (mm < 20)
    return {
      liters: litersPerAcre.toLocaleString(),
      buckets: bucketsPerAcre,
      description: t("Good soaking"),
    };
  return {
    liters: litersPerAcre.toLocaleString(),
    buckets: bucketsPerAcre,
    description: t("Heavy watering"),
  };
};

export const calculateIrrigationPlannerSummary = (planData, t) => {
  const irrigationDates = planData
    .filter((day) => day.irrigation_mm > 0)
    .map((day) => ({
      date: day.date,
      amount: day.irrigation_mm,
      converted: convertIrrigationMmToDescription(day.irrigation_mm, t),
      formattedDate: new Date(day.date).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    }));

  const totalIrrigationNeeded = planData.reduce(
    (sum, day) => sum + (day.irrigation_mm || 0),
    0
  );

  const totalRainfall = planData.reduce(
    (sum, day) => sum + (day.rainfall_mm || 0),
    0
  );

  const totalIrrigationConverted = convertIrrigationMmToDescription(
    totalIrrigationNeeded,
    t
  );
  const totalRainfallConverted = convertIrrigationMmToDescription(
    totalRainfall,
    t
  );

  return {
    irrigationDates: irrigationDates,
    totalIrrigationNeeded: totalIrrigationNeeded,
    totalRainfall: totalRainfall,
    totalIrrigationConverted: totalIrrigationConverted,
    totalRainfallConverted: totalRainfallConverted,
  };
};
